import { CanonicalLoad, equipmentLabel } from '@numeo/types-core'
import { fmtMiles, fmtWeight } from '../utils/format'
import { orderId } from '../utils/loadFields'
import { CopyButton } from './BoxTruckDrawerActions'

/** A numeric/short spec shown as a labelled card in the 2-col grid. */
const SpecCell = ({ label, value }: { label: string; value: string }) => (
    <div className="rounded-lg bg-background px-3 py-2.5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">{label}</div>
        <div className="mt-0.5 text-sm font-semibold text-foreground">{value}</div>
    </div>
)

/** A label/value row (with an optional copy button) — used for the non-Sylectus canonical fields. `wrap` lets a
 *  free-text value run onto more lines, right-aligned under the first, instead of being cut off with an ellipsis. */
const SpecRow = ({ label, value, copy, wrap }: { label: string; value: string; copy?: boolean; wrap?: boolean }) => (
    <div className={`flex justify-between gap-2 rounded-lg bg-background px-3 py-2 ${wrap ? 'items-start' : 'items-center'}`}>
        <span className="shrink-0 text-xs font-medium text-foreground-muted">{label}</span>
        <span className="flex min-w-0 items-center gap-1.5">
            <span className={`text-xs font-semibold text-foreground ${wrap ? 'break-words text-right' : 'truncate'}`}>{value}</span>
            {copy && <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />}
        </span>
    </div>
)

/** SYLECTUS_EMAIL specs — the original box-truck layout, unchanged: Miles/Pieces/Weight/Dimensions plus the
 *  four boolean requirement chips (Yes/No/—). Every value here comes from the `details.sylectus*` bag, which
 *  only SYLECTUS_EMAIL loads carry. */
const SylectusSpecs = ({ load }: { load: CanonicalLoad }) => {
    // Always shown with an explicit Yes/No (not just when true), so a dispatcher can read the load's
    // constraints at a glance. Null/absent → "—".
    const attrs: { label: string; value: boolean | null | undefined }[] = [
        { label: 'Hazmat', value: load.details?.sylectusHazmat },
        { label: 'Stackable', value: load.details?.sylectusStackable },
        { label: 'Dock Level', value: load.details?.sylectusDockLevel },
        { label: 'CSA/Fast Load', value: load.details?.sylectusCsaFastLoad },
    ]
    // The Sylectus order number, shown here in the detail (the table column was removed) — SYLECTUS_EMAIL only.
    const orderNo = orderId(load)
    return (
        <>
            {orderNo && (
                <div className="mt-2">
                    <SpecRow label="Order ID" value={orderNo} copy />
                </div>
            )}
            <div className="mt-2 grid grid-cols-2 gap-2">
                <SpecCell label="Miles" value={fmtMiles(load.distanceMiles)} />
                <SpecCell label="Pieces" value={load.pieceCount != null ? String(load.pieceCount) : '—'} />
                <SpecCell label="Weight" value={fmtWeight(load.weightLbs)} />
                <SpecCell label="Dimensions" value={load.details?.sylectusDimensions || '—'} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
                {attrs.map((a) => (
                    <div key={a.label} className="flex items-center justify-between rounded-lg bg-background px-3 py-2">
                        <span className="text-xs font-medium text-foreground-muted">{a.label}</span>
                        <span className={`text-xs font-semibold ${a.value ? 'text-success' : 'text-foreground-muted'}`}>
                            {a.value == null ? '—' : a.value ? 'Yes' : 'No'}
                        </span>
                    </div>
                ))}
            </div>
        </>
    )
}

/** Canonical (loadboard) specs for a NON-Sylectus box-truck load — DAT, TQL, Truckstop, Arrive, etc. Mirrors
 *  Load Hub's EquipmentDetailsSection field logic: the Sylectus-only requirement chips / Dimensions (which are
 *  always blank for these sources) are dropped in favour of the fields these sources actually carry on the
 *  canonical columns — a broker-quotable Reference ID, commodity, length, full/partial and requirements. */
const CanonicalSpecs = ({ load }: { load: CanonicalLoad }) => {
    // DAT / DAT_EXTENSION: the broker-quotable reference is `postersReferenceId` (e.g. "SW188"). It has NO
    // externalId fallback — the internal DAT id is meaningless to a broker, so a post that lacks a reference
    // shows "—" rather than pretending the externalId is one. Non-DAT sources use the loadboard-native `loadID`,
    // falling back to the prefix-stripped `externalId`.
    const isDat = load.sourceType === 'DAT' || load.sourceType === 'DAT_EXTENSION'
    const referenceId = (isDat ? load.postersReferenceId : (load.loadID?.toString() ?? load.externalId))?.trim() || null
    // What the truck IS, spelled out the way the source board spells it — `BR` reads "Straight Box Truck
    // Reefer". `equipmentLabel` answers with the raw code for anything it does not recognise, so a board whose
    // codes are its own shows what it posted rather than a wrong name.
    const truck = load.equipmentType ? equipmentLabel(load.equipmentType) : null
    return (
        <>
            <div className="mt-2 grid grid-cols-2 gap-2">
                <SpecCell label="Miles" value={fmtMiles(load.distanceMiles)} />
                <SpecCell label="Weight" value={fmtWeight(load.weightLbs)} />
                <SpecCell label="Length" value={load.equipmentLengthFt ? `${load.equipmentLengthFt} ft` : '—'} />
                <SpecCell label="Pieces" value={load.pieceCount != null ? String(load.pieceCount) : '—'} />
            </div>
            {/* Always rendered (with "—" when absent) so the panel keeps a stable shape across loads, rather
                than rows appearing/disappearing per load — mirrors the Sylectus chips' always-on behaviour. */}
            <div className="mt-2 space-y-1.5">
                <SpecRow label="Reference ID" value={referenceId || '—'} copy={!!referenceId} />
                <SpecRow label="Truck" value={truck || '—'} />
                {/* Wraps rather than truncates: a commodity is free text ("10 PLTS 30X42 PAINT AND RELATED
                    MATERIALS") and the part cut off is usually what the dispatcher needs. */}
                <SpecRow label="Commodity" value={load.commodity || '—'} wrap />
                <SpecRow label="Load" value={load.fullPartial || '—'} />
            </div>
            {/* Special requirements — Load Hub suppresses these for BEON (they duplicate the notes there). */}
            {load.specialRequirements && load.sourceType !== 'BEON' && (
                <div className="mt-2 rounded-lg bg-background px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Requirements</div>
                    <div className="mt-0.5 whitespace-pre-wrap text-[13px] font-medium text-foreground">{load.specialRequirements}</div>
                </div>
            )}
        </>
    )
}

/**
 * Source-aware Load Specifications block for the Box Truck detail drawer. SYLECTUS_EMAIL keeps its original
 * Sylectus-shaped layout (requirement chips + Dimensions from the `details.sylectus*` bag); every other source
 * renders the canonical loadboard fields (Reference ID, commodity, length, requirements), so a DAT/TQL/… load
 * no longer shows a block of blank Sylectus-only cells.
 */
export const LoadSpecifications = ({ load }: { load: CanonicalLoad }) => (
    <>
        <div className="mt-5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Load Specifications</div>
        {load.sourceType === 'SYLECTUS_EMAIL' ? <SylectusSpecs load={load} /> : <CanonicalSpecs load={load} />}
    </>
)
